import { ArrowRight, MapPin, Clock, Compass } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Layout from "@/components/Layout";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

export default function Home() {
  const { t } = useTranslation();
  const [loadHeroMotion, setLoadHeroMotion] = useState(false);
  const [heroMotionReady, setHeroMotionReady] = useState(false);

  useEffect(() => {
    const canPlayHeroMotion = window.matchMedia(
      "(min-width: 768px) and (prefers-reduced-motion: no-preference)"
    ).matches;
    if (!canPlayHeroMotion) return;

    const motionTimeout = window.setTimeout(
      () => setLoadHeroMotion(true),
      4500
    );
    return () => window.clearTimeout(motionTimeout);
  }, []);

  return (
    <Layout>
      <SEO
        title={t("home.seo.title")}
        description={t("home.seo.description")}
        keywords="Istanbul private tours, Turkey travel agency, Istanbul local guide, Bosphorus tour, Turkish food tour, custom Istanbul itinerary"
        preloadImage="/manus-storage/home-hero-poster_b186d532.avif"
      />

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Poster is the LCP-safe default; motion joins after the first view is settled. */}
        <div className="absolute inset-0 w-full h-full">
          <picture>
            <source
              srcSet="/manus-storage/home-hero-poster_b186d532.avif"
              type="image/avif"
            />
            <source
              srcSet="/manus-storage/home-hero-poster_1b89efc7.webp"
              type="image/webp"
            />
            <img
              src="/manus-storage/home-hero-poster_1b89efc7.webp"
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              decoding="async"
              width="1376"
              height="768"
              className="h-full w-full object-cover"
            />
          </picture>
          {loadHeroMotion && (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              onLoadedData={() => setHeroMotionReady(true)}
              aria-hidden="true"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${heroMotionReady ? "opacity-100" : "opacity-0"}`}
            >
              <source
                src="/manus-storage/home-hero-motion_b5b33069.mp4"
                type="video/mp4"
              />
            </video>
          )}
          <div className="premium-hero-overlay absolute inset-0" />
        </div>

        {/* Hero Content */}
        <div className="relative container h-full flex flex-col justify-center items-center text-center text-white pt-20">
          <span className="luxury-kicker text-sm md:text-base mb-6 animate-fade-in-up">
            {t("hero.subtitle")}
          </span>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-bold mb-7 leading-[0.98] max-w-5xl text-balance animate-fade-in-up animation-delay-200">
            {t("hero.title")}
          </h1>
          <div className="ornament-rule mb-7" aria-hidden="true">
            <span className="tulip-seal" />
          </div>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mb-10 leading-relaxed animate-fade-in-up animation-delay-400">
            {t("hero.description")}
          </p>
          <div className="flex flex-col gap-8 sm:flex-row sm:gap-4 animate-fade-in-up animation-delay-600">
            <Button
              asChild
              size="lg"
              className="bg-secondary text-primary hover:bg-secondary/90 px-8 py-6 text-lg rounded-full"
            >
              <Link href="/experiences">{t("hero.explore")}</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-primary px-8 py-6 text-lg rounded-full"
            >
              <Link href="/contact">{t("hero.plan")}</Link>
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-white/60">
          <div className="w-[1px] h-16 bg-gradient-to-b from-white to-transparent mx-auto mb-2"></div>
          <span className="text-xs tracking-widest uppercase">
            {t("common.scroll")}
          </span>
        </div>

        {/* Wavy Divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none rotate-180">
          <svg
            className="relative block w-[calc(100%+1.3px)] h-[60px] md:h-[100px]"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-background"
            ></path>
          </svg>
        </div>
      </section>

      {/* Introduction / About Section */}
      <section className="marble-section py-16 md:py-20 relative overflow-hidden">
        <div className="container max-w-6xl">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[5fr_7fr] lg:gap-14">
            <div className="relative flex justify-center">
              <div className="absolute -top-10 -left-10 w-64 h-64 bg-secondary/15 rounded-full blur-3xl"></div>
              <div className="heritage-arch relative z-10 overflow-hidden max-w-md mx-auto">
                <picture className="block">
                  <source
                    srcSet="/manus-storage/home-guide_89c363b6.avif"
                    type="image/avif"
                  />
                  <source
                    srcSet="/manus-storage/home-guide_7660443b.webp"
                    type="image/webp"
                  />
                  <img
                    src="/manus-storage/home-guide_7660443b.webp"
                    alt={t("homeGuide.imageAlt")}
                    loading="lazy"
                    decoding="async"
                    width="720"
                    height="1080"
                    className="h-auto w-full object-cover"
                  />
                </picture>
              </div>
            </div>

            <div className="max-w-2xl lg:justify-self-end">
              <span className="luxury-kicker text-sm mb-4 block">
                {t("about.subtitle")}
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 leading-tight">
                {t("about.title")}
              </h2>
              <div
                className="ornament-rule justify-start mb-6"
                aria-hidden="true"
              >
                <span className="tulip-seal" />
              </div>
              <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                {t("about.description")}
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex flex-col gap-2">
                  <span className="font-serif text-xl font-bold text-primary border-b border-secondary/45 pb-2">
                    {t("homeGuide.local.title")}
                  </span>
                  <span className="text-sm text-muted-foreground uppercase tracking-wide">
                    {t("homeGuide.local.description")}
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="font-serif text-xl font-bold text-primary border-b border-secondary/45 pb-2">
                    {t("homeGuide.personal.title")}
                  </span>
                  <span className="text-sm text-muted-foreground uppercase tracking-wide">
                    {t("homeGuide.personal.description")}
                  </span>
                </div>
              </div>

              <Link href="/about">
                <Button
                  variant="link"
                  className="text-primary hover:text-secondary p-0 text-lg font-serif italic group"
                >
                  {t("about.readMore")}{" "}
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="bg-primary py-18 md:py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="heritage-pattern absolute inset-0 opacity-30"
            aria-hidden="true"
          ></div>
        </div>

        <div className="container relative z-10">
          <div className="text-center mb-14 md:mb-16">
            <span className="luxury-kicker text-sm mb-4 block">
              {t("services.subtitle")}
            </span>
            <h2 className="font-serif text-4xl md:text-6xl font-bold text-white mb-8">
              {t("services.title")}
            </h2>
            <div className="ornament-rule" aria-hidden="true">
              <span className="tulip-seal" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Service 1: Tours */}
            <div className="interactive-frame heritage-panel group relative h-full rounded-3xl p-8">
              <div className="gold-icon-disc absolute -top-6 left-8 w-16 h-16 rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-transform duration-500">
                <MapPin size={32} strokeWidth={1.5} />
              </div>
              <div className="mt-8">
                <h3 className="font-serif text-2xl font-bold text-white mb-4">
                  {t("services.tours.title")}
                </h3>
                <p className="text-white/70 mb-8 leading-relaxed">
                  {t("services.tours.desc")}
                </p>
                <Link
                  href="/experiences"
                  className="inline-flex items-center gap-2 text-secondary font-medium tracking-wide group-hover:gap-4 transition-all"
                >
                  {t("common.exploreTours")} <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Service 2: Custom Trip Planning */}
            <div className="interactive-frame heritage-panel group relative h-full rounded-3xl p-8 md:translate-y-5">
              <div className="gold-icon-disc absolute -top-6 left-8 w-16 h-16 rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-transform duration-500">
                <Compass size={32} strokeWidth={1.5} />
              </div>
              <div className="mt-8">
                <h3 className="font-serif text-2xl font-bold text-white mb-4">
                  {t("services.planning.title")}
                </h3>
                <p className="text-white/70 mb-8 leading-relaxed">
                  {t("services.planning.desc")}
                </p>
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 text-secondary font-medium tracking-wide group-hover:gap-4 transition-all"
                >
                  {t("common.planTrip")} <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Service 3: VIP Transfers */}
            <div className="interactive-frame heritage-panel group relative h-full rounded-3xl p-8">
              <div className="gold-icon-disc absolute -top-6 left-8 w-16 h-16 rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-transform duration-500">
                <Clock size={32} strokeWidth={1.5} />
              </div>
              <div className="mt-8">
                <h3 className="font-serif text-2xl font-bold text-white mb-4">
                  {t("services.vip.title")}
                </h3>
                <p className="text-white/70 mb-8 leading-relaxed">
                  {t("services.vip.desc")}
                </p>
                <Link
                  href="/vip-transfers"
                  className="inline-flex items-center gap-2 text-secondary font-medium tracking-wide group-hover:gap-4 transition-all"
                >
                  {t("common.bookTransfer")} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Experiences Section */}
      <section className="marble-section py-16 md:py-20 relative">
        <div className="container">
          <div className="text-center mb-10 md:mb-12">
            <span className="luxury-kicker text-sm mb-3 block">
              {t("homeTours.eyebrow")}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6">
              {t("homeTours.title")}
            </h2>
            <div className="ornament-rule" aria-hidden="true">
              <span className="tulip-seal" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Experience Card 1 */}
            <Link href="/contact">
              <div className="group cursor-pointer">
                <div className="interactive-frame luxury-tour-card relative overflow-hidden rounded-2xl mb-4 aspect-[4/5]">
                  <picture className="block h-full w-full">
                    <source
                      srcSet="/manus-storage/home-bosphorus_d3410f3a.avif"
                      type="image/avif"
                    />
                    <source
                      srcSet="/manus-storage/home-bosphorus_661c0e22.webp"
                      type="image/webp"
                    />
                    <img
                      src="/manus-storage/home-bosphorus_661c0e22.webp"
                      alt={t("homeTours.bosphorus.title")}
                      loading="lazy"
                      decoding="async"
                      width="720"
                      height="401"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                  <div className="absolute top-4 right-4 border border-secondary/40 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-primary uppercase tracking-wider">
                    {t("homeTours.bestseller")}
                  </div>
                  <div className="absolute bottom-0 left-0 p-6 w-full transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-serif font-bold text-white mb-2">
                      {t("homeTours.bosphorus.title")}
                    </h3>
                    <p className="text-white/80 text-sm mb-4 line-clamp-2">
                      {t("homeTours.bosphorus.description")}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary font-bold">
                        {t("homeTours.bosphorus.price")}
                      </span>
                      <span className="text-white text-sm flex items-center gap-1">
                        {t("common.viewDetails")} <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Experience Card 2 */}
            <Link href="/contact">
              <div className="group cursor-pointer transform md:-translate-y-8">
                <div className="interactive-frame luxury-tour-card relative overflow-hidden rounded-2xl mb-4 aspect-[4/5]">
                  <picture className="block h-full w-full">
                    <source
                      srcSet="/manus-storage/home-bazaar_1659c72c.avif"
                      type="image/avif"
                    />
                    <source
                      srcSet="/manus-storage/home-bazaar_9b104547.webp"
                      type="image/webp"
                    />
                    <img
                      src="/manus-storage/home-bazaar_9b104547.webp"
                      alt={t("homeTours.bazaar.title")}
                      loading="lazy"
                      decoding="async"
                      width="720"
                      height="401"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 p-6 w-full transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-serif font-bold text-white mb-2">
                      {t("homeTours.bazaar.title")}
                    </h3>
                    <p className="text-white/80 text-sm mb-4 line-clamp-2">
                      {t("homeTours.bazaar.description")}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary font-bold">
                        {t("homeTours.bazaar.price")}
                      </span>
                      <span className="text-white text-sm flex items-center gap-1">
                        {t("common.viewDetails")} <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Experience Card 3 */}
            <Link href="/contact">
              <div className="group cursor-pointer">
                <div className="interactive-frame luxury-tour-card relative overflow-hidden rounded-2xl mb-4 aspect-[4/5]">
                  <picture className="block h-full w-full">
                    <source
                      srcSet="/manus-storage/home-cooking_8162802f.avif"
                      type="image/avif"
                    />
                    <source
                      srcSet="/manus-storage/home-cooking_35db6728.webp"
                      type="image/webp"
                    />
                    <img
                      src="/manus-storage/home-cooking_35db6728.webp"
                      alt={t("homeTours.culinary.title")}
                      loading="lazy"
                      decoding="async"
                      width="720"
                      height="401"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 p-6 w-full transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-serif font-bold text-white mb-2">
                      {t("homeTours.culinary.title")}
                    </h3>
                    <p className="text-white/80 text-sm mb-4 line-clamp-2">
                      {t("homeTours.culinary.description")}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-secondary font-bold">
                        {t("homeTours.culinary.price")}
                      </span>
                      <span className="text-white text-sm flex items-center gap-1">
                        {t("common.viewDetails")} <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <div className="text-center mt-12">
            <Link href="/experiences">
              <Button
                variant="outline"
                size="lg"
                className="rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-white transition-colors"
              >
                {t("homeTours.allTours")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
